import { z } from 'zod';

/**
 * Oturum tipleri — UC AYRI YETKI EKSENI.
 *
 * Bunlar tek bir `role` alanina KATLANMAZ; birbirinden bagimsiz sorulara cevap
 * verirler ve gecmiste tek alanda birlestirilmis her deneme yanlis ekran
 * gostermistir:
 *
 *   userType    : pazarin hangi tarafi        -> customer | exchange
 *   roleCode    : platform ayricaligi         -> super_admin | user
 *   memberType  : buro ICINDEKI otorite       -> admin | staff
 *
 * Ornek: bir `exchange` kullanicisi `user` rolunde ama kendi burosunda `admin`
 * olabilir; bir `super_admin` hicbir buronun uyesi degildir. Tek alan bu uc
 * durumu ayni anda ifade edemez.
 */

export const USER_TYPES = ['customer', 'exchange'] as const;
export type UserType = (typeof USER_TYPES)[number];

export const ROLE_CODES = ['super_admin', 'user'] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

export const MEMBER_TYPES = ['admin', 'staff'] as const;
export type MemberType = (typeof MEMBER_TYPES)[number];

/**
 * Personele VERILEBILEN yetki kodlari (grant tablosunda satiri olanlar).
 *
 * Yonetici yetkileri (manageMembers, managePermissions, editOrganization,
 * viewAuditLog) bilincli olarak BURADA YOK: sunucu bunlar icin grant satiri
 * yazmaz, yonetici ORTUK olarak tam yetkilidir. Bu yuzden bir yonetici asla
 * grant listesine karsi sinanmaz — bkz. hasPermission().
 */
export const PERMISSION_CODES = [
  'viewQuoteRequests',
  'createOffer',
  'editOffer',
  'completeTask',
  'viewAccounts',
  'manageAccounts',
  'viewReport',
  'createReport',
] as const;
export type PermissionCode = (typeof PERMISSION_CODES)[number];

/**
 * pg int8'i STRING olarak dondurur. Bu yuzden her kimlik alani `string`.
 * Number()'a cevirip === ile karsilastirmak 2^53 ustunde sessizce yanlis
 * sonuc verir; kimlikler hicbir yerde sayiya cevrilmez.
 */
const idSchema = z.union([z.string(), z.number()]).transform((value) => String(value));

export const sessionUserSchema = z.object({
  id: idSchema,
  userFullName: z.string().optional(),
  userEmail: z.string().optional(),
  userPhone: z.string().optional(),
  userCity: z.string().optional(),
  userDistrict: z.string().optional(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const organizationSummarySchema = z.object({
  id: idSchema,
  name: z.string().optional(),
  typeCode: z.string().optional(),
  memberType: z.enum(MEMBER_TYPES).optional(),
});
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

export const consentsSchema = z.object({
  kvkkAccepted: z.boolean(),
  usageAgreementAccepted: z.boolean(),
});
export type Consents = z.infer<typeof consentsSchema>;

/**
 * localStorage'a yazilan oturum ozeti.
 *
 * `/v1/me` UCU YOK. Soguk acilista kim oldugumuzu soracak bir yer olmadigi icin
 * bu ozet tek geri donus noktasi. Sema ile DOGRULANARAK okunur: localStorage
 * kullanicinin (ve XSS'in) yazabildigi bir alan; bozuk bir kayit sessizce
 * "yetkili" bir duruma cozulmemeli, cozulemezse oturum dusmeli.
 */
export const sessionSnapshotSchema = z.object({
  user: sessionUserSchema,
  permissions: z.array(z.string()),
  organization: organizationSummarySchema.nullable(),
  userType: z.enum(USER_TYPES),
  roleCode: z.enum(ROLE_CODES),
  memberType: z.enum(MEMBER_TYPES).nullable(),
  mustChangePassword: z.boolean(),
  consents: consentsSchema,
});
export type SessionSnapshot = z.infer<typeof sessionSnapshotSchema>;

/**
 * Yetki kontrolu — TEK yer.
 *
 * ORG YONETICISI GRANT LISTESINE KARSI SINANMAZ. Sunucu yonetici icin grant
 * satiri yazmadigindan liste bos gelebilir; listeye bakan bir kontrol yoneticiyi
 * kendi ekranlarindan kilitler.
 *
 * ORGANIZASYONU OLMAYAN KULLANICIDA YETKI KONTROLU YOK (2026-09-23, sunucuyla
 * hizalandi). Sunucu (currency-management postTaskOffer / postUpdateTask) grant
 * kapisini YALNIZ `callerOrganizationId` varsa uyguluyor: org'suz buro tek basina
 * calisan bir isletmedir ve teklif verir, tamamlar. Istemci eskiden burada HAYIR
 * diyordu; sonuc sunucunun izin verdigi isi arayuzun gizlemesiydi (menude
 * "Tekliflerim" yok, "Odeme alindi" dugmesi yok).
 *
 * Musteri tarafini etkilemez: musteri menusunde hicbir oge `permission` tasimiyor
 * (menus.ts), musteri bolgesindeki kapilar `adminOnly` -> isOrgAdmin ile calisiyor.
 * Asil denetim her durumda sunucuda; arayuzdeki bir hata en fazla calismayan bir
 * buton gosterir, sunucu yetkisiz istegi 403 ile reddeder.
 */
export const hasPermission = (
  snapshot: Pick<SessionSnapshot, 'organization' | 'memberType' | 'permissions'> | null,
  code: string,
): boolean => {
  if (!snapshot) return false;
  // Uyelik YOKSA grant sistemi hic calismaz — sunucu da kontrol etmiyor.
  if (!snapshot.organization) return true;
  if (snapshot.memberType === 'admin') return true;
  return snapshot.permissions.includes(code);
};

/**
 * Verilen yetkilerden EN AZ BIRI var mi? Tek kod icin hasPermission ile ayni kural.
 * Menude birden cok yetkinin ayni ekrana goturdugu yerler icin (ornek: Tekliflerim —
 * teklif veren, guncelleyen ve "Odeme alindi" diyen personelin ortak ekrani).
 */
export const hasAnyPermission = (
  snapshot: Pick<SessionSnapshot, 'organization' | 'memberType' | 'permissions'> | null,
  codes: readonly string[],
): boolean => codes.some((code) => hasPermission(snapshot, code));

/** Kullanici kendi organizasyonunun yoneticisi mi? (buro ya da musteri farketmez) */
export const isOrgAdmin = (snapshot: Pick<SessionSnapshot, 'memberType'> | null): boolean =>
  snapshot?.memberType === 'admin';

/**
 * Organizasyonun tipi: 'customer' | 'exchange'.
 *
 * `organizationSummarySchema.typeCode` uzun sure semada durup HIC OKUNMAMISTI; musteri
 * organizasyonu modeliyle (2026-08-30) anlam kazandi. Ayni ekranlar iki bolgede paylasildigi
 * icin (organization feature'i hem /buro hem /musteri altinda kayitli) bazi parcalar yalniz
 * buroda anlamli: yetki grant'lari, oda/sicil alanlari.
 */
export const orgTypeCode = (
  snapshot: Pick<SessionSnapshot, 'organization'> | null,
): string | null => snapshot?.organization?.typeCode ?? null;

/**
 * Musteri organizasyonunun yoneticisi mi?
 *
 * Bu kisi org'un TUM taleplerini gorur (sunucu kapsami genisletiyor) ama baskasinin talebinde
 * SALT OKUNURDUR — kazanan secimi talebi acan kisidedir. Arayuzde sahiplik kontrolu bu yuzden
 * ayrica yapilir; bu yardimci "org geneli gorunum acik mi" sorusunu cevaplar.
 */
export const isCustomerOrgAdmin = (
  snapshot: Pick<SessionSnapshot, 'organization' | 'memberType'> | null,
): boolean => orgTypeCode(snapshot) === 'customer' && snapshot?.memberType === 'admin';
